/* Privacy Center — Iter 44 · usuario ejerce sus derechos de Habeas Data. */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, Trash2, ShieldCheck, RefreshCw, Loader2,
  AlertTriangle, CheckCircle2, Mail, FileText,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { api, friendlyError } from '../lib/api';
import { useToast } from '../hooks/use-toast';

export default function PrivacyCenter() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [consents, setConsents] = useState([]);
  const [deletion, setDeletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([
        api.privacyMyConsents(),
        api.privacyDeletionStatus(),
      ]);
      setConsents(c.consents || []);
      setDeletion(d.pending ? d.request : null);
    } catch (e) {
      toast({ title: 'No pudimos cargar', description: friendlyError(e), variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadMyData = async () => {
    setDownloading(true);
    try {
      const data = await api.privacyExportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `habita-mis-datos-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Descarga iniciada', description: 'Guarda el archivo en un lugar seguro.' });
    } catch (e) {
      toast({ title: 'Error', description: friendlyError(e), variant: 'destructive' });
    } finally { setDownloading(false); }
  };

  const requestDeletion = async (e) => {
    e.preventDefault();
    if (!confirmEmail) return;
    setRequesting(true);
    try {
      await api.privacyRequestDeletion({ reason: deletionReason, confirm_email: confirmEmail });
      toast({ title: 'Solicitud enviada', description: 'Tienes 14 días para cancelarla si cambias de opinión.' });
      setDeletionReason('');
      setConfirmEmail('');
      await load();
    } catch (err) {
      toast({ title: 'No pudimos procesar', description: friendlyError(err), variant: 'destructive' });
    } finally { setRequesting(false); }
  };

  const cancelDeletion = async () => {
    try {
      await api.privacyCancelDeletion();
      toast({ title: 'Solicitud cancelada' });
      await load();
    } catch (err) {
      toast({ title: 'Error', description: friendlyError(err), variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-center">
      <SEO title="Centro de Privacidad · habita.co" description="Descarga tus datos, gestiona tu consentimiento y solicita la eliminación de tu cuenta." />
      <Header />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8">
          <Link to="/mi-cuenta" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-habita mb-3">
            <ArrowLeft className="w-4 h-4" /> Volver a Mi cuenta
          </Link>
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl bg-habita text-white flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></span>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-habita">Ley 1581 · Habeas Data</p>
              <h1 className="font-serif text-3xl font-bold text-habita-dark">Centro de Privacidad</h1>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3 max-w-2xl">Ejerce tus derechos como titular de datos: descarga tu información, actualiza tu perfil, revoca consentimientos o solicita la eliminación de tu cuenta.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8 space-y-6">
        {/* Download my data */}
        <section className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="privacy-download-section">
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 rounded-xl bg-habita-mint text-habita flex items-center justify-center shrink-0"><Download className="w-5 h-5" /></span>
            <div className="flex-1">
              <h2 className="font-serif text-xl font-bold text-habita-dark">Descarga tus datos</h2>
              <p className="text-sm text-gray-600 mt-1">Obtén un archivo JSON con tu perfil, publicaciones, favoritos, mensajes, historial de consentimientos y notificaciones. No incluye contraseñas.</p>
              <button
                onClick={downloadMyData}
                disabled={downloading}
                className="mt-4 h-10 px-5 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark disabled:opacity-60 flex items-center gap-2"
                data-testid="privacy-download-btn"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Descargar mis datos
              </button>
            </div>
          </div>
        </section>

        {/* Update profile */}
        <section className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="privacy-update-section">
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 rounded-xl bg-habita-mint text-habita flex items-center justify-center shrink-0"><RefreshCw className="w-5 h-5" /></span>
            <div className="flex-1">
              <h2 className="font-serif text-xl font-bold text-habita-dark">Actualiza tu información</h2>
              <p className="text-sm text-gray-600 mt-1">Corrige tu nombre, teléfono o preferencias desde tu cuenta.</p>
              <Link to="/mi-cuenta" className="mt-4 inline-flex h-10 px-5 rounded-full bg-white text-habita border border-habita hover:bg-habita/5 font-semibold items-center gap-2">
                Editar perfil
              </Link>
            </div>
          </div>
        </section>

        {/* Consent history */}
        <section className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="privacy-consents-section">
          <div className="flex items-start gap-4 mb-4">
            <span className="w-10 h-10 rounded-xl bg-habita-mint text-habita flex items-center justify-center shrink-0"><FileText className="w-5 h-5" /></span>
            <div className="flex-1">
              <h2 className="font-serif text-xl font-bold text-habita-dark">Historial de consentimientos</h2>
              <p className="text-sm text-gray-600 mt-1">Registro auditable de las autorizaciones que has otorgado.</p>
            </div>
          </div>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : consents.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Sin consentimientos registrados.</p>
          ) : (
            <ul className="space-y-2" data-testid="privacy-consent-list">
              {consents.slice(0, 20).map(c => (
                <li key={c.id} className="text-xs border border-gray-100 rounded-lg p-3 flex flex-wrap items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider text-[10px] ${c.accepted ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    {c.kind}
                  </span>
                  <span className="text-gray-700">Política v{c.policy_version}</span>
                  <span className="text-gray-400 ml-auto">{new Date(c.created_at).toLocaleString('es-CO')}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Deletion */}
        <section className="bg-white border border-rose-100 rounded-2xl p-6" data-testid="privacy-delete-section">
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><Trash2 className="w-5 h-5" /></span>
            <div className="flex-1">
              <h2 className="font-serif text-xl font-bold text-habita-dark">Eliminar mi cuenta</h2>
              <p className="text-sm text-gray-600 mt-1">Al eliminar tu cuenta se retiran tus datos personales del sistema (sujeto a obligaciones legales de conservación). Tienes 14 días para cancelar la solicitud.</p>

              {deletion ? (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm" data-testid="privacy-delete-pending">
                  <p className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Solicitud pendiente</p>
                  <p className="text-xs text-amber-700 mt-1">Creada el {new Date(deletion.created_at).toLocaleString('es-CO')}.</p>
                  <button
                    onClick={cancelDeletion}
                    className="mt-3 h-9 px-4 rounded-full bg-white text-amber-700 border border-amber-300 hover:bg-amber-100 font-semibold text-xs"
                    data-testid="privacy-cancel-delete"
                  >
                    Cancelar solicitud
                  </button>
                </div>
              ) : (
                <form onSubmit={requestDeletion} className="mt-4 space-y-3" data-testid="privacy-delete-form">
                  <textarea
                    value={deletionReason}
                    onChange={e => setDeletionReason(e.target.value)}
                    placeholder="Motivo (opcional)"
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-rose-400 outline-none text-sm"
                    data-testid="privacy-delete-reason"
                    maxLength={500}
                  />
                  <input
                    type="email"
                    value={confirmEmail}
                    onChange={e => setConfirmEmail(e.target.value)}
                    placeholder={`Escribe ${user?.email || 'tu email'} para confirmar`}
                    className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-rose-400 outline-none text-sm"
                    data-testid="privacy-delete-confirm-email"
                    required
                  />
                  <button
                    type="submit"
                    disabled={requesting || !confirmEmail}
                    className="h-10 px-5 rounded-full bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-40 flex items-center gap-2"
                    data-testid="privacy-delete-submit"
                  >
                    {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Solicitar eliminación
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Habeas Data form link */}
        <section className="bg-habita-mint/40 border border-habita/20 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 rounded-xl bg-habita text-white flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></span>
            <div className="flex-1">
              <h2 className="font-serif text-xl font-bold text-habita-dark">¿Necesitas otra ayuda?</h2>
              <p className="text-sm text-gray-700 mt-1">Si tu solicitud es más compleja o eres representante legal, usa el formulario público de Habeas Data.</p>
              <Link to="/habeas-data" className="mt-4 inline-flex h-10 px-5 rounded-full bg-habita text-white hover:bg-habita-dark font-semibold items-center gap-2">
                Formulario Habeas Data <CheckCircle2 className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
